import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Box, Skeleton } from '@mui/material';
import { getAuthUser } from './auth/session';
import { AssistantProvider } from './assistant/AssistantContext';
import AssistantAnswer from './assistant/AssistantAnswer';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';

const Landing = lazy(() => import('./components/Landing/Landing'));
const Login = lazy(() => import('./components/Authentication/Login'));
const Register = lazy(() => import('./components/Authentication/Register'));
const Market = lazy(() => import('./components/Listings/Listings'));
const ListingDetail = lazy(() => import('./components/Listings/ListingDetail'));
const PostItem = lazy(() => import('./components/Posting/PostItem'));
const Cart = lazy(() => import('./components/Cart/Cart'));
const Checkout = lazy(() => import('./components/Cart/Checkout'));
const Payment = lazy(() => import('./components/Cart/Payment'));
const Notifications = lazy(() => import('./components/Notifications/Notifications'));
const RateTransaction = lazy(() => import('./components/Ratings/RateTransaction'));
const Feedback = lazy(() => import('./components/Ratings/Feedback'));
const Sell = lazy(() => import('./components/Sell/Sell'));
const Profile = lazy(() => import('./components/Profile/Profile'));
const Inventory = lazy(() => import('./components/Inventory/Inventory'));
const Orders = lazy(() => import('./components/Orders/Orders'));
const BuildOrder = lazy(() => import('./components/Orders/BuildOrder'));
const Buy = lazy(() => import('./components/Buy/Buy'));
const SharedList = lazy(() => import('./components/Lists/SharedList'));
const Grocery = lazy(() => import('./components/Grocery/Grocery'));
const GroceryItemDetail = lazy(() => import('./components/Grocery/GroceryItemDetail'));
const Capture = lazy(() => import('./components/Capture/Capture'));
const Services = lazy(() => import('./components/Services/Services'));
const ServiceOfferForm = lazy(() => import('./components/Services/ServiceOfferForm'));
const ServiceOfferDetail = lazy(() => import('./components/Services/ServiceOfferDetail'));
const ServiceRequestDetail = lazy(() => import('./components/Services/ServiceRequestDetail'));
const Privacy = lazy(() => import('./components/Legal/Privacy'));
const Terms = lazy(() => import('./components/Legal/Terms'));

function PageFallback() {
  // Most chunks arrive in well under 200ms; showing a skeleton for those
  // reads as a flash. Only draw one when the wait is actually noticeable.
  const [show, setShow] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setShow(true), 220);
    return () => window.clearTimeout(timer);
  }, []);
  if (!show) return <Box aria-hidden="true" sx={{ minHeight: '50vh' }} />;
  return (
    <Box role="status" aria-label="Loading page" className="needl-fade-in" sx={{ px: { xs: 2, md: 3 }, py: { xs: 1.5, md: 2 } }}>
      <Skeleton width={160} height={30} />
      <Skeleton width={320} height={20} sx={{ mb: 2 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', gap: 1.5 }}>
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" sx={{ aspectRatio: '1 / 1.3', height: 'auto' }} />
        ))}
      </Box>
    </Box>
  );
}

function HomeRoute() {
  return getAuthUser() ? <Navigate to="/market" replace /> : <Landing />;
}

function InventoryRoute() {
  const [params] = useSearchParams();
  const list = String(params.get('list') || '').trim().toLowerCase();
  return list === 'groceries' ? <Grocery /> : <Inventory />;
}

function LegacyGroceryItemRedirect() {
  const { listId, itemId } = useParams();
  return (
    <Navigate
      to={`/inventory/lists/groceries/${encodeURIComponent(String(listId || ''))}/items/${encodeURIComponent(String(itemId || ''))}`}
      replace
    />
  );
}

// Each route change fades the new page in over ~150ms so content never pops.
function RouteFade({ children }) {
  const { pathname } = useLocation();
  return <div key={pathname} className="needl-fade-in">{children}</div>;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/market" element={<Market />} />
      <Route path="/listings" element={<Navigate to="/market" replace />} />
      <Route path="/post-item" element={<PostItem />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/payment" element={<Payment />} />
      <Route path="/notifications" element={<Notifications />} />
      <Route path="/rate" element={<RateTransaction />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/sell" element={<Sell />} />
      <Route path="/capture" element={<Capture />} />
      <Route path="/buy" element={<Buy />} />
      <Route path="/lists" element={<Navigate to="/buy" replace />} />
      <Route path="/lists/:listId" element={<Navigate to="/buy" replace />} />
      <Route path="/shared-list" element={<SharedList />} />
      <Route path="/grocery" element={<Navigate to="/inventory?list=groceries" replace />} />
      <Route path="/grocery/lists/:listId/items/:itemId" element={<LegacyGroceryItemRedirect />} />
      <Route path="/services" element={<Services />} />
      <Route path="/services/offer" element={<ServiceOfferForm />} />
      <Route path="/services/offers/:uuid" element={<ServiceOfferDetail />} />
      <Route path="/services/requests/:uuid" element={<ServiceRequestDetail />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/inventory" element={<InventoryRoute />} />
      <Route path="/inventory/lists/groceries/:listId/items/:itemId" element={<GroceryItemDetail />} />
      <Route path="/orders" element={<Orders />} />
      <Route path="/orders/build/:orderId/checkout" element={<BuildOrder mode="checkout" />} />
      <Route path="/orders/build/:orderId/items/:itemId" element={<BuildOrder mode="detail" />} />
      <Route path="/orders/build/:orderId" element={<BuildOrder />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/listing/:uuid" element={<ListingDetail />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AssistantProvider>
        <ScrollToTop />
        <Header />
        <div className="needl-app-content" data-testid="app-content">
          <main style={{ flex: 1, minWidth: 0 }}>
            <AssistantAnswer />
            <Suspense fallback={<PageFallback />}>
              <RouteFade>
                <AppRoutes />
              </RouteFade>
            </Suspense>
          </main>
          <Footer />
        </div>
      </AssistantProvider>
    </Router>
  );
}

export default App;
